import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ChevronLeft, LifeBuoy, Megaphone, Send, ShoppingBag, Sparkles, TrendingUp } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useShopPath } from '@/hooks/useShopPath';
import { useAssistant } from '@services/AssistantProvider';

/**
 * 360px AI assistant panel that sits between the sidebar and page content
 * while open (the sidebar collapses to a 60px rail to make room). Messages
 * are driven by the shared `AssistantProvider`, which posts to the real
 * `/api/assistant` Bedrock + Knowledge Base endpoint and threads shop context
 * and recent history through every request — the same wiring the onboarding
 * assistant uses.
 *
 * The header sparkle uses the shared `.shimmer-border` keyframe so the AI
 * affordance reads as alive. The intro state offers four suggested prompts;
 * clicking one sends it through the same round-trip. Assistant replies are
 * rendered as markdown and any Knowledge Base sources are surfaced as links.
 */
const INTRO =
	"Hi 👋 I'm your Unify AI Assistant. Ask me about your orders, channels, ads or anything else — or pick a suggestion below to get started.";

const SUGGESTED_PROMPTS = [
	{
		icon: ShoppingBag,
		label: 'Show my orders from last month',
		prompt: 'Show me a list of all orders from last month.'
	},
	{
		icon: Megaphone,
		label: 'Is Local Ads right for my store?',
		prompt: 'Is Local Ads something that would work well for my store?'
	},
	{
		icon: TrendingUp,
		label: 'How is my storefront performing?',
		prompt: 'How is my storefront performing this month?'
	},
	{
		icon: LifeBuoy,
		label: 'Where can I find my billing information?',
		prompt: 'Where can I find my billing information?'
	}
];

export function AIChatPanel({ onCollapse, isOpen = true }) {
	const { messages, sendMessage, isLoading } = useAssistant();
	const { shopPath } = useShopPath();
	const [input, setInput] = useState('');
	const scrollRef = useRef(null);
	const wasOpenRef = useRef(false);
	const started = messages.length > 0;

	// Render the assistant's markdown links: in-app dashboard paths become
	// shop-scoped React Router links (so they navigate within the SPA and keep
	// the chain's `:shopId` prefix), external/Knowledge Base links open in a new
	// tab.
	const markdownComponents = useMemo(
		() => ({
			a: ({ href, children }) => {
				const isInternal = typeof href === 'string' && href.startsWith('/') && !href.startsWith('//');
				if (isInternal) {
					return <Link to={shopPath(href)}>{children}</Link>;
				}
				return (
					<a href={href} target="_blank" rel="noopener noreferrer">
						{children}
					</a>
				);
			}
		}),
		[shopPath]
	);

	// Keep the conversation pinned to the latest message. History is restored
	// from localStorage while the panel is closed (width 0), so a smooth scroll
	// then never takes — the user would land on the first question on open
	// (NEA-5907). So: only scroll while the panel is open, jump instantly the
	// moment it opens (land on the latest message), and smooth-scroll only for
	// messages that arrive while it's already open.
	useEffect(() => {
		const el = scrollRef.current;
		if (!el || !isOpen) {
			if (!isOpen) wasOpenRef.current = false;
			return;
		}
		const justOpened = !wasOpenRef.current;
		el.scrollTo({ top: el.scrollHeight, behavior: justOpened ? 'auto' : 'smooth' });
		wasOpenRef.current = true;
	}, [messages, isLoading, isOpen]);

	const send = (e) => {
		e?.preventDefault();
		const text = input.trim();
		if (!text || isLoading) return;
		setInput('');
		sendMessage(text);
	};

	return (
		<aside className="flex h-screen w-[360px] flex-col border-r border-border bg-white">
			<header className="flex items-center justify-between border-b border-border px-4 pb-4 pt-7">
				<div className="flex items-center gap-2">
					<div className="shimmer-border flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground">
						<Sparkles className="relative z-10 h-5 w-5" />
					</div>
					<span className="text-base font-bold text-foreground">AI Assistant</span>
				</div>
				<button
					type="button"
					onClick={onCollapse}
					aria-label="Close chat"
					className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted"
				>
					<ChevronLeft className="h-5 w-5" />
				</button>
			</header>

			<div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
				{!started ? <p className="px-1 text-sm text-muted-foreground">{INTRO}</p> : null}

				{messages.map((m) =>
					m.role === 'assistant' ? (
						<div key={m.id} className="flex items-start gap-2">
							<div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50">
								<Sparkles className="h-4 w-4 text-blue-600" />
							</div>
							<div
								className={cn(
									'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm',
									m.isError ? 'bg-destructive/10 text-destructive' : 'bg-muted text-foreground'
								)}
							>
								<div className="[&_a]:font-medium [&_a]:text-blue-600 [&_a]:underline [&_li]:my-0.5 [&_p+p]:mt-2 [&_p]:m-0 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-4">
									<ReactMarkdown components={markdownComponents}>{m.content}</ReactMarkdown>
								</div>
								{m.sources?.length ? (
									<div className="mt-2 flex flex-col gap-1 border-t border-border/60 pt-2">
										<span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
											Relevant links
										</span>
										{m.sources.map((source, i) => (
											<a
												key={i}
												href={source.url}
												target="_blank"
												rel="noopener noreferrer"
												className="truncate text-xs font-medium text-blue-600 hover:underline"
											>
												{source.title || source.url}
											</a>
										))}
									</div>
								) : null}
							</div>
						</div>
					) : (
						<div key={m.id} className="flex justify-end">
							<div className="max-w-[85%] rounded-2xl bg-blue-600 px-3.5 py-2.5 text-sm text-white">{m.content}</div>
						</div>
					)
				)}

				{isLoading ? (
					<div className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground" aria-live="polite">
						<span className="inline-flex h-1.5 w-1.5 animate-bounce rounded-full bg-blue-400" />
						Thinking…
					</div>
				) : null}

				{!started ? (
					<div className="space-y-2 pt-2">
						<p className="px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Try asking</p>
						{SUGGESTED_PROMPTS.map((s) => {
							const Icon = s.icon;
							return (
								<button
									key={s.label}
									type="button"
									onClick={() => !isLoading && sendMessage(s.prompt)}
									className="flex w-full items-center gap-2.5 rounded-xl border border-border bg-white px-3 py-2.5 text-left transition-colors hover:border-blue-200 hover:bg-muted"
								>
									<div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50">
										<Icon className="h-3.5 w-3.5 text-blue-600" />
									</div>
									<span className="text-sm leading-snug text-foreground">{s.label}</span>
								</button>
							);
						})}
					</div>
				) : null}
			</div>

			<form onSubmit={send} className="flex items-center gap-2 border-t border-border p-3">
				<input
					type="text"
					value={input}
					onChange={(e) => setInput(e.target.value)}
					placeholder="Ask a question…"
					className="h-10 flex-1 rounded-lg border border-border bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
				/>
				<button
					type="submit"
					disabled={isLoading || !input.trim()}
					aria-label="Send"
					className={cn(
						'flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600 transition-colors hover:bg-blue-200',
						(isLoading || !input.trim()) && 'opacity-50'
					)}
				>
					<Send className="h-4 w-4" />
				</button>
			</form>
		</aside>
	);
}
